/**
 * order-money.ts  —  Money-clarity helper (R1, Phase 2 redesign)
 *
 * Derives a human-readable money state from an order's payment fields.
 * Returns i18n key references + interpolation params; never hardcodes
 * display strings. The caller resolves them with `t(key, params)`.
 *
 * Logic (from spec):
 *   balance = max(0, requiredAmount − paidAmount)   (upfront amount still due)
 *   codBalance = max(0, totalAmount − paidAmount)   (cash to collect on delivery)
 *   headline = totalAmount  (formatted EGP amount)
 *
 * State priority:
 *   1. cancelled/declined  → paidAmount>0 ? "Refunded" : "No payment"  (grey)
 *   2. COD                 → "Collect EGP {codBalance} cash"           (amber)
 *   3. paidAmount ≥ totalAmount → "Paid in full"                        (green)
 *   3b. paidAmount ≥ requiredAmount → "Collect EGP {codBalance} cash"   (amber)
 *       (deposit cleared online, remainder still due on delivery)
 *   4. paidAmount > 0      → "EGP {paid} paid · EGP {balance} due"      (amber)
 *   5. else                → "Awaiting payment"                          (amber)
 */

import type { OrderStatus, PaymentMethod } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";

export type MoneyTone = "grey" | "green" | "amber" | "red";

export type MoneyState = {
  /** i18n key for the one-line money description shown under the total */
  lineKey: string;
  /** Interpolation params passed to t(lineKey, params) */
  lineParams: Record<string, string | number>;
  /** Visual tone for the line text */
  tone: MoneyTone;
  /** CSS color hex/value matching the tone  */
  color: string;
  /** The pay-chip label key (COD label, Paid online, Part paid, Unpaid) */
  chipKey: string;
  /** Chip visual tone */
  chipTone: MoneyTone;
  /** Chip CSS color */
  chipColor: string;
  /** The formatted headline total (EGP {totalAmount}) */
  headlineFormatted: string;
};

/** Format a numeric EGP amount per spec: EGP {Math.round(n).toLocaleString('en-US')} */
export function formatEgp(amount: number): string {
  return `EGP ${Math.round(amount).toLocaleString("en-US")}`;
}

export const CANCELLED_STATUSES = new Set<OrderStatus>([
  "cancelled",
  "declined",
]);

/**
 * Derive the money-clarity state from order payment fields.
 *
 * @param status        Raw order status
 * @param paymentMethod "COD" or "EXTERNAL_ONLINE"
 * @param paidAmount    Amount already paid by buyer
 * @param requiredAmount  Amount required upfront (deposit or full)
 * @param totalAmount   Headline order total
 */
export function getMoneyState(
  status: OrderStatus,
  paymentMethod: PaymentMethod,
  paidAmount: number,
  requiredAmount: number,
  totalAmount: number,
): MoneyState {
  const balance = Math.max(0, requiredAmount - paidAmount);
  const headlineFormatted = formatEgp(totalAmount);

  // ── 0. Draft — pre-submission, payment is meaningless ───────────────────
  if (status === "draft") {
    return {
      lineKey: keys.orders.moneyNotStarted,
      lineParams: {},
      tone: "grey",
      color: "#a59f90",
      chipKey: keys.orders.moneyNotStarted,
      chipTone: "grey",
      chipColor: "#a59f90",
      headlineFormatted,
    };
  }

  // ── 1. Cancelled / declined ──────────────────────────────────────────────
  if (CANCELLED_STATUSES.has(status)) {
    if (paidAmount > 0) {
      return {
        lineKey: keys.orders.moneyRefunded,
        lineParams: {},
        tone: "grey",
        color: "#a59f90",
        chipKey: keys.orders.moneyChipRefunded,
        chipTone: "grey",
        chipColor: "#a59f90",
        headlineFormatted,
      };
    }
    return {
      lineKey: keys.orders.moneyNoPayment,
      lineParams: {},
      tone: "grey",
      color: "#a59f90",
      chipKey: keys.orders.moneyChipNoPayment,
      chipTone: "grey",
      chipColor: "#a59f90",
      headlineFormatted,
    };
  }

  // ── 2. COD ───────────────────────────────────────────────────────────────
  if (paymentMethod === "COD") {
    // Cash collected on delivery covers the whole remaining total, not just
    // the upfront required amount (which is only deposit + shipping).
    const codBalance = Math.max(0, totalAmount - paidAmount);
    return {
      lineKey: keys.orders.moneyCodCollect,
      lineParams: { balance: formatEgp(codBalance) },
      tone: "amber",
      color: "var(--accent-600)",
      chipKey: keys.orders.moneyChipCod,
      chipTone: "green",
      chipColor: "var(--success-500)",
      headlineFormatted,
    };
  }

  // ── 3. Paid in full ──────────────────────────────────────────────────────
  // "In full" means the ORDER TOTAL is covered — not merely the upfront
  // `requiredAmount`, which on a deposit store is just the deposit (+shipping).
  // Clearing a 60 EGP deposit on a 140 EGP order is not payment in full: the
  // seller still has 80 EGP to collect on delivery, and telling them the order
  // is paid (in green) is how a courier hands over goods without taking the cash.
  if (totalAmount > 0 && paidAmount >= totalAmount) {
    return {
      lineKey: keys.orders.moneyPaidInFull,
      lineParams: {},
      tone: "green",
      color: "var(--success-500)",
      chipKey: keys.orders.moneyChipPaidOnline,
      chipTone: "green",
      chipColor: "#2563eb",
      headlineFormatted,
    };
  }

  // ── 3b. Upfront amount cleared, balance still due on delivery ────────────
  // The buyer owes nothing more online, so this is NOT "part paid / due" —
  // it is a deposit-paid order with cash to collect at the door.
  if (requiredAmount > 0 && paidAmount >= requiredAmount) {
    return {
      lineKey: keys.orders.moneyCodCollect,
      lineParams: { balance: formatEgp(Math.max(0, totalAmount - paidAmount)) },
      tone: "amber",
      color: "var(--accent-600)",
      chipKey: keys.orders.moneyChipPartPaid,
      chipTone: "amber",
      chipColor: "var(--accent-600)",
      headlineFormatted,
    };
  }

  // ── 4. Partial payment ───────────────────────────────────────────────────
  if (paidAmount > 0) {
    return {
      lineKey: keys.orders.moneyPartPaid,
      lineParams: { paid: formatEgp(paidAmount), balance: formatEgp(balance) },
      tone: "amber",
      color: "var(--accent-600)",
      chipKey: keys.orders.moneyChipPartPaid,
      chipTone: "amber",
      chipColor: "var(--accent-600)",
      headlineFormatted,
    };
  }

  // ── 5. Awaiting payment (default) ────────────────────────────────────────
  return {
    lineKey: keys.orders.moneyAwaiting,
    lineParams: {},
    tone: "amber",
    color: "var(--accent-600)",
    chipKey: keys.orders.moneyChipUnpaid,
    chipTone: "red",
    chipColor: "var(--error-500)",
    headlineFormatted,
  };
}
